// src/components/BoardPack/pages/new_stack_chart.jsx
import React, { useState, useEffect } from "react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { useQueue } from "@/context/QueueContext";
import NewStackedChart from "@/components/BoardPack/components/charts/NewStackedChart";

const NewStackChartPage = ({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
}) => {
  const { enqueue } = useQueue();

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // ── Transform API response → chart-ready arrays ──────────────────────────
  const transformApiData = (apiData) => {
    const meta          = apiData?.meta        ?? {};
    const dataObj       = apiData?.data        ?? {};
    const percentageObj = apiData?.percentages ?? {};

    // helper: safely parse "12,277" or "26%" → number
    const parseNum = (val) => {
      if (val == null) return null;
      const parsed = parseFloat(String(val).replace(/[,%]/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    const dataKeys = Object.keys(dataObj);
    if (!dataKeys.length) return null;

    const appWebKey = dataKeys[0];
    const pndKey    = dataKeys[1];

    const appWebObj = dataObj[appWebKey] ?? {};
    const pndObj    = dataObj[pndKey]    ?? {};

    const years = Object.keys(appWebObj).filter((k) => /^\d{4}$/.test(k)).sort();

    const selectedYear  = String(meta.year  ?? year);
    const selectedMonth = String(meta.month ?? month);

    const months         = [];
    const yearRanges     = [];
    const pndData        = [];
    const appWebData     = [];
    const appToTotalData = [];

    years.forEach((yr) => {
      let span = 0;
      MONTHS.forEach((mon) => {
        if (yr === selectedYear) {
          const selectedMonthIdx = MONTHS.indexOf(selectedMonth);
          const currentMonthIdx  = MONTHS.indexOf(mon);
          if (selectedMonthIdx !== -1 && currentMonthIdx > selectedMonthIdx) return;
        }

        const appVal = appWebObj?.[yr]?.[mon];
        const pndVal = pndObj?.[yr]?.[mon];
        if (appVal == null && pndVal == null) return;

        months.push(mon);
        appWebData.push(parseNum(appVal));
        pndData.push(parseNum(pndVal));

        // ✅ FIX: percentages come as "35%" → parseNum gives 35 → keep as-is (percent form)
        // Do NOT divide by 100 — NewStackedChart expects percent values e.g. 35, 48
        const ratioRaw = parseNum(percentageObj?.[yr]?.[mon]);
        appToTotalData.push(ratioRaw);

        span++;
      });
      if (span > 0) yearRanges.push({ year: yr, span });
    });

    const measure        = meta.measure         ?? "";
    const percentageLbl  = meta.percentage_label ?? "App to Total %";

    const pageLabels = page?.labels ?? {};
    const appWebLabel = pageLabels.revenue     ?? `${appWebKey}_${measure}`;
    const pndLabel    = pageLabels.transaction ?? `${pndKey}_${measure}`;
    const ratioLabel  = pageLabels.atv         ?? percentageLbl;

    return {
      months, yearRanges, pndData, appWebData, appToTotalData,
      appWebLabel, pndLabel, ratioLabel,
    };
  };

  useEffect(() => {
    // if (!year || !month || !contract) return;
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const rawParams = page?.params ?? {};
      

      


    const response = {
    "success": true,
    "meta": {
        "contract": "0020013",
        "year": 2026,
        "prevYear": 2025,
        "month": "Apr",
        "value_type": 1,
        "measure": "Ticket_Count",
        "percentage_label": "App to Ticket Count%"
    },
    "data": {
        "APP/WEB": {
            "2025": {
                "Jan": "46,449",
                "Feb": "52,199",
                "Mar": "88,127",
                "Apr": "45,843",
                "May": "51,035",
                "Jun": "49,970",
                "Jul": "58,108",
                "Aug": "58,961",
                "Sep": "51,781",
                "Oct": "51,869",
                "Nov": "55,983",
                "Dec": "59,218"
            },
            "2026": {
                "Jan": "60,724",
                "Feb": "73,991",
                "Mar": "85,890",
                "Apr": "13,136"
            }
        },
        "PND": {
            "2025": {
                "Jan": "84,003",
                "Feb": "94,921",
                "Mar": "131,082",
                "Apr": "70,115",
                "May": "74,574",
                "Jun": "77,844",
                "Jul": "86,324",
                "Aug": "83,040",
                "Sep": "72,452",
                "Oct": "72,355",
                "Nov": "77,943",
                "Dec": "78,265"
            },
            "2026": {
                "Jan": "79,394",
                "Feb": "88,275",
                "Mar": "96,192",
                "Apr": "15,342"
            }
        }
    },
    "percentages": {
        "2025": {
            "Jan": "36%",
            "Feb": "35%",
            "Mar": "40%",
            "Apr": "40%",
            "May": "41%",
            "Jun": "39%",
            "Jul": "40%",
            "Aug": "42%",
            "Sep": "42%",
            "Oct": "42%",
            "Nov": "42%",
            "Dec": "43%"
        },
        "2026": {
            "Jan": "43%",
            "Feb": "46%",
            "Mar": "47%",
            "Apr": "46%"
        }
    }
};

      if (response?.success) {
        const transformed = transformApiData(response);
        setChartData(transformed);
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching payment source KPI data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;
  if (!chartData) return <ErrorState error="No data available." onRetry={fetchData} />;

  return (
    <NewStackedChart
      title={page?.title}
      subtitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      yAxisLabel={page?.yAxisLabel}
      bottomLabel={page?.bottomLabel}
      months={chartData.months}
      yearRanges={chartData.yearRanges}
      pndData={chartData.pndData}
      appWebData={chartData.appWebData}
      appToTotalData={chartData.appToTotalData}
      colors={page?.colors}
      appWebLabel={chartData.appWebLabel}
      pndLabel={chartData.pndLabel}
      ratioLabel={chartData.ratioLabel}
      showDataLabels={page?.showDataLabels ?? true}
      showRamadanRow={page?.showRamadanRow ?? true}
    />
  );
};

export default NewStackChartPage;